import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { newsList, newsCategories } from '@/mocks/news';
import NewsCard from '@/components/feature/NewsCard';
import Seo from '@/components/feature/Seo';

const PAGE_STEP = 6;

export default function News() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(PAGE_STEP);

  const filtered = useMemo(() => {
    const key = query.trim().toLowerCase();
    return newsList.filter((article) => {
      const categoryOk = activeCategory === '全部' || article.category === activeCategory;
      const keywordOk =
        !key ||
        article.title.toLowerCase().includes(key) ||
        article.summary.toLowerCase().includes(key);
      return categoryOk && keywordOk;
    });
  }, [activeCategory, query]);

  const visibleList = filtered.slice(0, visible);

  const handleCategory = (category: string) => {
    setActiveCategory(category);
    setVisible(PAGE_STEP);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(keyword);
    setVisible(PAGE_STEP);
  };

  return (
    <div className="w-full">
      <Seo
        title="影视资讯 - 上司的老婆影视剧内容站"
        description="影视行业动态、剧集资讯、演员动态与幕后花絮，第一时间了解影视圈正在发生的事，掌握最新剧集与行业走向。"
        keywords="影视资讯,行业动态,剧集资讯,演员动态,幕后花絮"
      />

      <section className="bg-primary-950 text-background-50">
        <div className="px-4 md:px-6 lg:px-10 py-12 md:py-16">
          <nav className="flex items-center gap-1.5 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">影视资讯</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-black">影视资讯</h1>
          <p className="text-sm md:text-base text-background-300 mt-3 max-w-2xl leading-relaxed">
            汇聚行业动态、剧集资讯、演员动态与幕后花絮，用更短的时间，了解影视圈正在发生的事。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            {newsCategories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => handleCategory(category)}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          <form onSubmit={handleSearch} className="relative lg:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索资讯关键词"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </form>
        </div>

        {visibleList.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {visibleList.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
            {visible < filtered.length ? (
              <div className="flex justify-center mt-10">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_STEP)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-down-line"></i>
                  加载更多资讯
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
              <i className="ri-search-eye-line"></i>
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-900">没有找到相关资讯</h2>
            <p className="text-sm text-foreground-500 mt-2 max-w-md">换个关键词试试，或者清除筛选条件查看全部资讯。</p>
            <button
              type="button"
              onClick={() => { setKeyword(''); setQuery(''); setActiveCategory('全部'); setVisible(PAGE_STEP); }}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              查看全部资讯
            </button>
          </div>
        )}
      </section>
    </div>
  );
}